import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Group } from "@mantine/core";
import {
  IconZoomIn,
  IconZoomOut,
  IconRotate,
  IconRotateClockwise,
  IconRefresh,
} from "@tabler/icons-react";
import "./ImageViewer.scss";

// Constants
const MIN_SCALE = 0.1;
const MAX_SCALE = 10.0; // 1000%
const SCALE_STEP = 0.2;
const INITIAL_SCALE = 1.0; // Start at 100%

interface ImageViewerProps {
  src: string;
  onLoad?: (dimensions: { width: number; height: number }) => void;
  simplifiedControls?: boolean; // Hide zoom/rotate controls
  disableDrag?: boolean; // Disable pan/drag functionality
}

const ImageViewer: React.FC<ImageViewerProps> = ({
  src,
  onLoad,
  simplifiedControls = false,
  disableDrag = false,
}) => {
  const [scale, setScale] = useState(INITIAL_SCALE);
  const [rotation, setRotation] = useState(0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [naturalDimensions, setNaturalDimensions] = useState({
    width: 0,
    height: 0,
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    const img = new Image();
    img.src = src;
    img.onload = () => {
      setNaturalDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
      onLoad?.({ width: img.naturalWidth, height: img.naturalHeight });

      if (containerRef.current) {
        if (
          scale === INITIAL_SCALE &&
          naturalDimensions.width === 0 &&
          naturalDimensions.height === 0
        ) {
          setScale(1.0);
        }
      }
    };
  }, [src, onLoad]);

  useEffect(() => {}, [scale]);

  const handleZoomIn = useCallback(() => {
    setScale((prevScale) => {
      const newScale = prevScale * (1 + SCALE_STEP);
      const finalScale = Math.min(newScale, MAX_SCALE);
      return finalScale;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prevScale) => {
      const newScale = prevScale / (1 + SCALE_STEP);
      const finalScale = Math.max(newScale, MIN_SCALE);
      return finalScale;
    });
  }, []);

  const handleRotateLeft = () => {
    setRotation((current) => (current - 90) % 360);
  };

  const handleRotateRight = () => {
    setRotation((current) => (current + 90) % 360);
  };

  const resetView = useCallback(() => {
    setScale(INITIAL_SCALE);
    setRotation(0);
    setPosition({ x: 0, y: 0 });
  }, [naturalDimensions]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disableDrag) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (disableDrag || !isDragging) return;
    e.preventDefault();

    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;

    setPosition({
      x: newX,
      y: newY,
    });
  };

  const handleMouseUp = () => {
    if (disableDrag) return;
    setIsDragging(false);
  };

  return (
    <div className="image-viewer">
      {!simplifiedControls && (
        <div className="controls-container">
          <Group justify="center" gap="xs">
            <Button
              size="sm"
              onClick={handleZoomOut}
              disabled={scale <= MIN_SCALE}
            >
              <IconZoomOut size={16} />
            </Button>
            <span>{Math.ceil(scale * 100)}%</span>
            <Button
              size="sm"
              onClick={handleZoomIn}
              disabled={scale >= MAX_SCALE}
            >
              <IconZoomIn size={16} />
            </Button>
            <Button size="sm" onClick={handleRotateLeft}>
              <IconRotate size={16} />
            </Button>
            <Button size="sm" onClick={handleRotateRight}>
              <IconRotateClockwise size={16} />
            </Button>
            <Button size="sm" onClick={resetView}>
              <IconRefresh size={16} />
            </Button>
          </Group>
        </div>
      )}

      <div className="content-container">
        <div
          ref={containerRef}
          className={`image-container ${isDragging ? "dragging" : ""} ${disableDrag ? "drag-disabled" : ""}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <img
            ref={imageRef}
            src={src}
            alt="Preview"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg) scale(${scale})`,
              transformOrigin: "center center",
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              userSelect: "none",
              transition: isDragging ? "none" : "transform 0.2s ease",
            }}
            draggable={false}
          />
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
